'use client';

import Image from 'next/image';
import Link from 'next/link';
import type { ProductCardProps } from '@routeroyal/theme-core';

export function RouteRoyalProductCard({
  product,
  locale = 'en',
  onAddToCart,
  className = '',
}: ProductCardProps) {
  const defaultVariant = product.variants[0];
  const price = defaultVariant?.price ?? product.minPrice;
  const compareAtPrice = defaultVariant?.compareAtPrice;
  const currency = defaultVariant?.currency ?? product.currency ?? 'GBP';

  const formatPrice = (amount: number) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).format(amount);

  return (
    <article
      className={`group bg-white rounded-xl overflow-hidden border border-gray-200 hover:border-gray-300 hover:shadow-lg transition-all duration-300 ${className}`}
    >
      <Link href={`/${locale}/shop/products/${product.handle}`} className="block">
        <div className="relative h-56 overflow-hidden bg-gray-100">
          {product.thumbnailUrl ? (
            <Image
              src={product.thumbnailUrl}
              alt={product.title}
              fill
              className="object-cover group-hover:scale-105 transition-transform duration-300"
              sizes="(max-width: 768px) 100vw, 400px"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400">
              <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
              </svg>
            </div>
          )}
          {compareAtPrice && compareAtPrice > (price ?? 0) && (
            <div className="absolute top-3 left-3">
              <span className="px-2 py-1 text-xs font-semibold text-white bg-red-600 rounded-md">
                Sale
              </span>
            </div>
          )}
        </div>
      </Link>

      <div className="p-4">
        <Link href={`/${locale}/shop/products/${product.handle}`}>
          {product.vendor && (
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">
              {product.vendor}
            </p>
          )}
          <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 group-hover:text-blue-700 transition-colors">
            {product.title}
          </h3>
        </Link>

        <div className="mt-2 flex items-baseline gap-2">
          {price != null && (
            <span className="text-lg font-bold text-gray-900">
              {formatPrice(price)}
            </span>
          )}
          {compareAtPrice != null && compareAtPrice > (price ?? 0) && (
            <span className="text-sm text-gray-400 line-through">
              {formatPrice(compareAtPrice)}
            </span>
          )}
        </div>

        {product.averageRating != null && product.reviewCount > 0 && (
          <div className="mt-2 flex items-center gap-1 text-sm text-gray-500">
            <span className="text-yellow-500">★</span>
            <span>{product.averageRating.toFixed(1)}</span>
            <span className="text-gray-400">({product.reviewCount})</span>
          </div>
        )}

        {onAddToCart && defaultVariant && (
          <button
            onClick={(e) => {
              e.preventDefault();
              onAddToCart(defaultVariant.id);
            }}
            className="mt-3 w-full rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 transition-colors"
          >
            Add to Cart
          </button>
        )}
      </div>
    </article>
  );
}
